import { useEffect, useState } from "react";
import {
    Link,
    useNavigate,
} from "react-router-dom";
import { toast } from "sonner";

import {
    Button,
} from "@/components/ui/button";
import {
    Input,
} from "@/components/ui/input";
import {
    Label,
} from "@/components/ui/label";
import {
    RadioGroup,
    RadioGroupItem,
} from "@/components/ui/radio-group";

import Layout from "@/components/Layout";
import {
    useAuth,
} from "@/contexts/AuthContext";
import {
    useLang,
} from "@/contexts/LangContext";
import {
    formatApiError,
} from "@/lib/api";


export default function Register() {
    const {
        t,
        lang,
    } = useLang();

    const {
        register,
        verifyRegistration,
        resendRegistrationCode,
    } = useAuth();

    const navigate = useNavigate();

    const [form, setForm] = useState({
        email: "",
        password: "",
        name: "",
        phone: "",
        role: "customer",
    });

    const [loading, setLoading] =
        useState(false);

    const [step, setStep] =
        useState("register");

    const [
        verificationId,
        setVerificationId,
    ] = useState("");

    const [
        verificationDestination,
        setVerificationDestination,
    ] = useState("");

    const [code, setCode] =
        useState("");

    const [devOtp, setDevOtp] =
        useState("");

    const [resendCooldown, setResendCooldown] =
        useState(0);

    useEffect(() => {
        if (
            step !== "verify"
            || resendCooldown <= 0
        ) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setResendCooldown(
                (value) => Math.max(value - 1, 0)
            );
        }, 1000);

        return () => {
            window.clearTimeout(timer);
        };
    }, [step, resendCooldown]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = await register(
                form
            );

            if (
                data.verification_required
            ) {
                setVerificationId(
                    data.verification_id
                );

                setVerificationDestination(
                    data.destination || ""
                );

                setDevOtp(
                    data.dev_otp || ""
                );

                setCode("");

                setResendCooldown(
                    Number(
                        data.resend_cooldown_seconds
                        ?? 60
                    )
                );

                setStep("verify");

                toast.success(
                    lang === "ar"
                        ? "تم إرسال رمز التحقق إلى بريدك الإلكتروني"
                        : "Verification code sent to your email"
                );

                return;
            }

            navigate(
                form.role === "seller"
                    ? "/seller/verification"
                    : "/"
            );

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                ) || err.message
            );

        } finally {
            setLoading(false);
        }
    };

    const onVerify = async (e) => {
        e.preventDefault();

        if (!/^\d{6}$/.test(code)) {
            toast.error(
                lang === "ar"
                    ? "أدخل رمز التحقق المكون من 6 أرقام"
                    : "Enter the 6-digit verification code"
            );
            return;
        }

        setLoading(true);

        try {
            await verifyRegistration(
                verificationId,
                code
            );

            toast.success(
                lang === "ar"
                    ? "تم تفعيل حسابك بنجاح"
                    : "Your account has been verified"
            );

            navigate(
                form.role === "seller"
                    ? "/seller/verification"
                    : "/"
            );

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                ) || err.message
            );

        } finally {
            setLoading(false);
        }
    };

    const onResend = async () => {
        if (!verificationId) {
            return;
        }

        setLoading(true);

        try {
            const data =
                await resendRegistrationCode(
                    verificationId
                );

            setVerificationDestination(
                data.destination
                || verificationDestination
            );

            setDevOtp(
                data.dev_otp || ""
            );

            setCode("");

            setResendCooldown(
                Number(
                    data.resend_cooldown_seconds
                    ?? 60
                )
            );

            toast.success(
                lang === "ar"
                    ? "تم إرسال رمز جديد"
                    : "A new code has been sent"
            );

        } catch (err) {
            toast.error(
                formatApiError(
                    err.response?.data?.detail
                ) || err.message
            );

        } finally {
            setLoading(false);
        }
    };

    const backToRegistration = () => {
        setStep("register");
        setVerificationId("");
        setVerificationDestination("");
        setCode("");
        setDevOtp("");
        setResendCooldown(0);
    };

    return (
        <Layout>
            <div className="max-w-md mx-auto px-4 py-12">
                <div className="bg-white border border-border rounded-3xl p-8 md:p-10 shadow-sm">

                    {step === "register" ? (
                        <>
                            <h1 className="text-3xl font-black text-espresso">
                                {t("register")}
                            </h1>

                            <p className="text-sm text-muted-foreground mt-2">
                                {t("appName")}
                            </p>

                            <form
                                onSubmit={onSubmit}
                                className="space-y-4 mt-8"
                                data-testid="register-form"
                            >
                                <div>
                                    <Label>
                                        {t("namePlaceholder")}
                                    </Label>

                                    <Input
                                        required
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                name:
                                                    e.target.value,
                                            })
                                        }
                                        data-testid="name-input"
                                        className="mt-1.5"
                                    />
                                </div>

                                <div>
                                    <Label>
                                        {t("emailPlaceholder")}
                                    </Label>

                                    <Input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                email:
                                                    e.target.value,
                                            })
                                        }
                                        data-testid="email-input"
                                        className="mt-1.5"
                                    />
                                </div>

                                <div>
                                    <Label>
                                        {lang === "ar" ? "رقم الجوال" : "Mobile number"}
                                        <span className="text-red-500">
                                            {" "}*
                                        </span>
                                    </Label>

                                    <Input
                                        type="tel"
                                        required
                                        inputMode="tel"
                                        autoComplete="tel"
                                        placeholder="05XXXXXXXX"
                                        value={form.phone}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                phone:
                                                    e.target.value,
                                            })
                                        }
                                        data-testid="phone-input"
                                        className="mt-1.5"
                                    />

                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        {lang === "ar"
                                            ? "رقم جوال سعودي صالح مطلوب لإنشاء الحساب."
                                            : "A valid Saudi mobile number is required."}
                                    </p>
                                </div>

                                <div>
                                    <Label>
                                        {t("passwordPlaceholder")}
                                    </Label>

                                    <Input
                                        type="password"
                                        required
                                        minLength={6}
                                        value={form.password}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                password:
                                                    e.target.value,
                                            })
                                        }
                                        data-testid="password-input"
                                        className="mt-1.5"
                                    />
                                </div>

                                <RadioGroup
                                    value={form.role}
                                    onValueChange={(v) =>
                                        setForm({
                                            ...form,
                                            role: v,
                                        })
                                    }
                                    className="grid grid-cols-2 gap-3"
                                >
                                    <label
                                        className={`p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                                            form.role === "customer"
                                                ? "border-terracotta bg-terracotta/5"
                                                : "border-border"
                                        }`}
                                        data-testid="role-customer"
                                    >
                                        <RadioGroupItem
                                            value="customer"
                                            className="sr-only"
                                        />
                                        <div className="font-bold text-sm text-espresso">
                                            {t("registerAsCustomer")}
                                        </div>
                                    </label>

                                    <label
                                        className={`p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                                            form.role === "seller"
                                                ? "border-terracotta bg-terracotta/5"
                                                : "border-border"
                                        }`}
                                        data-testid="role-seller"
                                    >
                                        <RadioGroupItem
                                            value="seller"
                                            className="sr-only"
                                        />
                                        <div className="font-bold text-sm text-espresso">
                                            {t("registerAsSeller")}
                                        </div>
                                    </label>
                                </RadioGroup>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white font-bold"
                                    data-testid="register-submit-btn"
                                >
                                    {loading
                                        ? "..."
                                        : t("register")}
                                </Button>
                            </form>

                            <div className="text-center text-sm text-muted-foreground mt-6">
                                <Link
                                    to="/login"
                                    className="text-terracotta font-bold hover:underline"
                                >
                                    {t("login")}
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <h1 className="text-3xl font-black text-espresso">
                                {lang === "ar"
                                    ? "تأكيد الحساب"
                                    : "Verify your account"}
                            </h1>

                            <p className="text-sm text-muted-foreground mt-3">
                                {lang === "ar"
                                    ? "أرسلنا رمز تحقق مكونًا من 6 أرقام إلى:"
                                    : "We sent a 6-digit verification code to:"}
                            </p>

                            <div className="mt-2 font-bold text-espresso">
                                {verificationDestination}
                            </div>

                            {devOtp && (
                                <div className="mt-5 rounded-2xl border border-yellow-300 bg-yellow-50 p-4 text-sm">
                                    <div className="font-bold">
                                        DEV OTP
                                    </div>
                                    <div className="text-2xl font-black tracking-[0.35em] mt-2">
                                        {devOtp}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-2">
                                        {lang === "ar"
                                            ? "يظهر هذا الرمز فقط لأن OTP_DEV_MODE مفعّل محليًا. لا تفعّله في Railway."
                                            : "This is visible only because local OTP_DEV_MODE is enabled. Never enable it in Railway."}
                                    </div>
                                </div>
                            )}

                            <form
                                onSubmit={onVerify}
                                className="space-y-4 mt-7"
                            >
                                <div>
                                    <Label>
                                        {lang === "ar"
                                            ? "رمز التحقق"
                                            : "Verification code"}
                                    </Label>
                                    <Input
                                        required
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        value={code}
                                        onChange={(e) =>
                                            setCode(
                                                e.target.value
                                                    .replace(/\D/g, "")
                                                    .slice(0, 6)
                                            )
                                        }
                                        placeholder="000000"
                                        className="mt-1.5 text-center text-2xl tracking-[0.35em] font-bold"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white font-bold"
                                >
                                    {loading
                                        ? "..."
                                        : lang === "ar"
                                        ? "تأكيد الرمز"
                                        : "Verify code"}
                                </Button>
                            </form>

                            <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-sm">
                                <button
                                    type="button"
                                    disabled={
                                        loading
                                        || resendCooldown > 0
                                    }
                                    onClick={onResend}
                                    className="text-terracotta font-bold hover:underline disabled:opacity-50 disabled:no-underline"
                                >
                                    {resendCooldown > 0
                                        ? (
                                            lang === "ar"
                                                ? `\u0625\u0639\u0627\u062f\u0629 \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0631\u0645\u0632 \u0628\u0639\u062f ${resendCooldown} \u062b\u0627\u0646\u064a\u0629`
                                                : `Resend code in ${resendCooldown}s`
                                        )
                                        : (
                                            lang === "ar"
                                                ? "\u0625\u0639\u0627\u062f\u0629 \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0631\u0645\u0632"
                                                : "Resend code"
                                        )}
                                </button>

                                <button
                                    type="button"
                                    disabled={loading}
                                    onClick={backToRegistration}
                                    className="text-muted-foreground hover:text-espresso"
                                >
                                    {lang === "ar"
                                        ? "تعديل البيانات"
                                        : "Edit details"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
}
